import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { ViewDataState } from 'src/types/Domain';
import serviceContainer from 'src/ServiceContainer';
import { cacheCheckFetchPivotData } from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { ListDataOptions } from 'src/worker/pivotWorker.types';
import { ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';

export interface ListViewConfig {
  grid: TenantConfigViewData;
  unmodifiedViewDefn: TenantConfigViewData;
  listSort: TenantConfigViewData;
  list: TenantConfigViewData;
  subheaderRollUp: TenantConfigViewData;
}

export interface ListViewSlice {
  viewDefns: ListViewConfig;
  isConfigLoading: boolean;
  viewDataState: ViewDataState;
  cacheHash: string | null;
}

const initialState: ListViewSlice = {
  viewDefns: {
    grid: {} as TenantConfigViewData,
    unmodifiedViewDefn: {} as TenantConfigViewData,
    listSort: {} as TenantConfigViewData,
    list: {} as TenantConfigViewData,
    subheaderRollUp: {} as TenantConfigViewData,
  },
  isConfigLoading: false,
  viewDataState: ViewDataState.idle,
  cacheHash: null,
};

const listViewReducer = createSlice({
  name: 'ListView',
  initialState,
  reducers: {
    requestConfig(state) {
      state.isConfigLoading = true;
    },
    receiveConfig(state, action: PayloadAction<ListViewConfig>) {
      state.isConfigLoading = false;
      state.viewDefns = action.payload;
    },
    receiveSomeListViewConfig(state, action: PayloadAction<TenantConfigViewData>) {
      state.isConfigLoading = false;
      state.viewDefns.grid = {
        ...state.viewDefns.grid,
        ...action.payload,
      };
    },
    requestData(state) {
      state.viewDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveCacheHash(state, action: PayloadAction<string>) {
      state.cacheHash = action.payload;
    },
    receiveCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.cacheHash) {
        state.viewDataState = ViewDataState.liveDataReady;
      }
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
    cleanUp() {
      return initialState;
    },
  },
});

export const {
  requestConfig,
  receiveConfig,
  receiveSomeListViewConfig,
  requestData,
  receiveCacheHash,
  receiveCachedData,
  receiveLiveData,
  receiveError,
  cleanUp,
} = listViewReducer.actions;

export function fetchListViewData(modelDefn: string, options: ListDataOptions) {
  // TODO: does this need to be configured with dataApi to handle preconfigured aggBys?
  return cacheCheckFetchPivotData(
    serviceContainer.pivotService.listDataCacheCheck(modelDefn, options),
    requestData,
    receiveCacheHash,
    receiveCachedData,
    receiveLiveData
  );
}

export default listViewReducer.reducer;
